import { nextTick, onMounted, onUnmounted } from 'vue';
import type VueRouter from 'vue-router';
import { useRouter } from 'vue-router/composables';

const resetBodyFocus = () => {
  document.body.tabIndex = 0;
  document.body.focus();
  document.body.tabIndex = -1;
};

/**
 * Reset the keyboard focus to the body tag on route changes.
 * Single page applications can cause focus to be lost when navigating between routes,
 * which can be disorienting for keyboard and assistive technology users.
 * This composable registers a router afterEach hook that moves the focus to the body tag whenever the route path changes.
 * @param routerInstance Optional VueRouter instance to use. If not provided, the composable will attempt to use the router instance from the current context.
 */
export function useRouterFocusReset(routerInstance?: VueRouter) {
  let removeAfterEachHook: (() => void) | undefined;

  onMounted(() => {
    const router = routerInstance ?? useRouter();

    if (!router?.afterEach) {
      return;
    }

    removeAfterEachHook = router.afterEach((to, from) => {
      if (to.path !== from.path) {
        nextTick(resetBodyFocus);
      }
    });
  });

  onUnmounted(() => {
    removeAfterEachHook?.();
  });
}
